import { memo, useCallback, useMemo } from 'react';

import Path from 'path-to-regexp';
import type { RouteComponentProps } from 'react-router';
import { withRouter } from 'react-router-dom';

import { Footer } from '@/components/layouts';
import { FormsBatch } from '@/components/ui/formsBatch/formsBatch';
import { ROUTE_WORKFLOW_OVERVIEW } from '@/constant/routes';
import type { TraceAction, WorkflowContextType } from '@/types/workflow.types';

const BATCH_MAX_FORMS = 10 as const;

type Props = {
  action: TraceAction;
  workflowContext: WorkflowContextType;
  toggleSignDialog: () => void;
  disabled: boolean;
  setFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  setCleanupFn: (fn: () => void) => void;
} & RouteComponentProps<
  { wfid: string },
  Record<string, never>,
  { from: string }
>;

export const Batch = memo((props: Props) => {
  const goBack = useCallback(() => {
    if (props.location.state && props.location.state.from) {
      props.history.push(props.location.state.from);
      return;
    }
    // by default go back to workflow overview
    props.history.push(
      Path.compile(ROUTE_WORKFLOW_OVERVIEW)({ id: props.match.params.wfid })
    );
  }, [props.match, props.location]);

  const handleSubmit = useCallback(
    (data, fn) => {
      props.setFormData(data);
      props.setCleanupFn(fn);
      props.toggleSignDialog();
    },
    [props.setFormData, props.setCleanupFn, props.toggleSignDialog]
  );

  const { rowId: workflowRowId } = props.workflowContext;
  const formsBatchCachingKey = useMemo(
    () => `formsBatch_${workflowRowId}_${props.location.search}`,
    [workflowRowId, props.location.search]
  );

  const schema = useMemo(
    () =>
      props.action.form
        ? props.action.form.schema
        : {
            type: 'object',
            title: props.action.title,
            description: props.action.description
          },
    [props.action]
  );

  const uiSchema = useMemo(
    () => (props.action.form ? props.action.form.uiSchema : {}),
    [props.action]
  );

  return (
    <div className="mt-subnavbar flex h-[calc(100vh-var(--spacing-full-navbar)-var(--workspace-frame-headroom,0px))] w-full flex-wrap items-start overflow-y-auto bg-background">
      <div className="flex size-full flex-col flex-nowrap justify-between bg-background">
        <FormsBatch
          // @ts-expect-error error since FormsBatch is not typed
          cachedUpdatesKey={formsBatchCachingKey}
          maxForms={BATCH_MAX_FORMS}
          onCancel={goBack}
          onSubmit={handleSubmit}
          schema={schema}
          submitDisabled={props.disabled}
          uiSchema={uiSchema}
          workflowContext={props.workflowContext}
        />
        <Footer />
      </div>
    </div>
  );
});

export default withRouter(Batch);
